import React, { useState } from 'react'
import Swal from 'sweetalert2'
import withReactContent from 'sweetalert2-react-content'

import { PageLink, PageTitle } from '../../../../../_metronic/layout/core'
import { FiltersExternalUserFlags } from '../../components/Filters'
import { IFilterExternalUserFlags } from '../../components/Filters/Filters.types'
import { fetchExternalUserFlagsLogs } from '../../api/fetchLogs'
import { convertToUTCByMSK } from '../../../../../utils'
import { getDefaultDateRange } from '../../../../../utils/getDefaultDateRange'
import { downloadFile } from '../../../../../utils/downloadFile'
import { isApiErrorExternalUserFlags } from '../../api/errors'
import { getErrorResponse } from '../../../../common/api/errors'

const AppSwal = withReactContent(Swal)

const breadCrumbs: Array<PageLink> = [
  {
    title: 'Логи изменений флагов',
    path: '/logs/external-user-flags',
    isSeparator: false,
    isActive: true,
  },
]

export const ExternalUserFlags = () => {
  const { startDate: defaultStartDate, endDate: defaultEndDate } = getDefaultDateRange()
  const initialFilterState = { dateStart: defaultStartDate, dateEnd: defaultEndDate }

  const [filters, setFilters] = useState<IFilterExternalUserFlags>(initialFilterState)
  const [isFiltersApplied, setFiltersApplied] = useState<boolean>(true)
  const [isLoading, setIsLoading] = useState(false)

  const handleApplyFilters = async () => {
    // Эта функция больше не используется для кнопки "Фильтровать",
    // но оставлена для совместимости с интерфейсом IFiltersExternalUserFlagsProps
    setFiltersApplied(true)
  }

  const handleFiltersChange = async (newFilters: IFilterExternalUserFlags) => {
    setFilters(newFilters)

    const validFilters = Object.entries(newFilters).filter(([_, value]) => !!value)
    const newKeys = validFilters.map(([key]) => key)
    const initialKeys = Object.keys(initialFilterState)

    if (newKeys.length === initialKeys.length && newKeys.some((key) => initialKeys.includes(key))) {
      setFiltersApplied(true)
    } else {
      setFiltersApplied(false)
    }
  }

  const handleResetFilters = async () => {
    const newFilters = {
      dateStart: defaultStartDate,
      dateEnd: defaultEndDate,
    }

    setFilters(newFilters)
    setFiltersApplied(true)
  }

  const exportExternalUserFlagsLogs = async () => {
    // Показываем индикатор загрузки
    const loadingSwal = AppSwal.fire({
      title: 'Экспорт в процессе...',
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    })

    setIsLoading(true)

    try {
      const params = {
        dateStart: filters.dateStart ? convertToUTCByMSK(filters.dateStart) : null,
        dateEnd: filters.dateEnd ? convertToUTCByMSK(filters.dateEnd) : null,
        userId: filters.userId || undefined,
      }

      const response = await fetchExternalUserFlagsLogs(params)
      
      // Проверяем тип ответа
      const contentType = response.headers['content-type']
      
      if (contentType && contentType.includes('application/json')) {
        // Если получили JSON вместо blob, значит это ошибка
        const reader = new FileReader()
        reader.onload = function() {
          try {
            const jsonResponse = JSON.parse(reader.result as string)
            console.error('Получен JSON вместо blob:', jsonResponse)
            
            let errorMessage = 'Ошибка при экспорте данных'
            
            if (jsonResponse.errors) {
              if (jsonResponse.errors.general_error) {
                errorMessage = jsonResponse.errors.general_error
              } else if (jsonResponse.errors.userId) {
                errorMessage = `Ошибка в поле ID клиента: ${jsonResponse.errors.userId}`
              } else {
                // Получаем первую ошибку из объекта errors
                const errorKeys = Object.keys(jsonResponse.errors)
                if (errorKeys.length > 0) {
                  const firstKey = errorKeys[0]
                  errorMessage = `${firstKey}: ${jsonResponse.errors[firstKey]}`
                }
              }
            }
            
            AppSwal.fire({
              icon: 'error',
              title: 'Ошибка',
              text: errorMessage,
            })
          } catch (e) {
            console.error('Ошибка при парсинге JSON:', e)
            AppSwal.fire({
              icon: 'error',
              title: 'Ошибка',
              text: 'Не удалось обработать ответ сервера',
            })
          }
        }
        reader.readAsText(response.data)
      } else {
        // Если получили blob, значит это успешный ответ
        downloadFile(response.data, 'external_user_flags', 'xlsx')
      }
    } catch (error: any) {
      let errorMessage = 'Не удалось экспортировать данные'
      
      // Детальное логирование ошибки для отладки
      console.error('Полная ошибка:', error)
      console.error('Тип ошибки:', typeof error)
      console.error('Статус ошибки:', error?.response?.status)
      console.error('Данные ошибки:', error?.response?.data)
      
      // Проверяем наличие response с данными
      if (error?.response?.data) {
        const responseData = error.response.data
        console.error('Данные ответа:', responseData)
        
        if (responseData.errors) {
          console.error('Найдены ошибки в ответе:', responseData.errors)
          
          if (responseData.errors.general_error) {
            errorMessage = responseData.errors.general_error
          } else if (responseData.errors.userId) {
            errorMessage = `Ошибка в поле ID клиента: ${responseData.errors.userId}`
          } else {
            // Получаем первую ошибку из объекта errors
            const errorKeys = Object.keys(responseData.errors)
            if (errorKeys.length > 0) {
              const firstKey = errorKeys[0]
              errorMessage = `${firstKey}: ${responseData.errors[firstKey]}`
            }
          }
        }
      } else if (isApiErrorExternalUserFlags(error)) {
        // Обработка ошибки через наш обработчик
        const errorData = error.data
        console.error('Данные из isApiErrorExternalUserFlags:', errorData)
        
        if (errorData && errorData.errors) {
          if (errorData.errors.general_error) {
            errorMessage = errorData.errors.general_error
          } else if (errorData.errors.userId) {
            errorMessage = `Ошибка в поле ID клиента: ${errorData.errors.userId}`
          } else {
            // Получаем первую ошибку из объекта errors
            const errorKeys = Object.keys(errorData.errors)
            if (errorKeys.length > 0) {
              const firstKey = errorKeys[0]
              errorMessage = `${firstKey}: ${errorData.errors[firstKey]}`
            }
          }
        }
      } else {
        // Попытка получить ошибку через общий обработчик
        const errorResponse = getErrorResponse<any>(error)
        console.error('Данные из getErrorResponse:', errorResponse)
        
        if (errorResponse) {
          if (errorResponse.general_error) {
            errorMessage = errorResponse.general_error
          } else if (errorResponse.message) {
            errorMessage = errorResponse.message
          }
        }
      }
      
      // Сначала закрываем индикатор загрузки
      AppSwal.close()
      setIsLoading(false)
      
      // Показываем ошибку в Swal
      console.error('Показываем ошибку в Swal:', errorMessage)
      return AppSwal.fire({
        icon: 'error',
        title: 'Ошибка',
        text: errorMessage,
        allowOutsideClick: true,
        confirmButtonText: 'Понятно'
      })
    } finally {
      setIsLoading(false)
      // В случае успеха закрываем индикатор загрузки
      AppSwal.close()
    }
  }

  return (
    <div>
      <div className='card sticky-top-tickets mb-7'>
        <PageTitle breadcrumbs={breadCrumbs} />
        <div className='card-header border-0 pt-5 pb-2'>
          <h3 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bold fs-3 mb-1'>Логи изменений флагов</span>
          </h3>
        </div>
      </div>

      <div className='card mb-7'>
        <div className='card-body border-0 pt-5'>
          <FiltersExternalUserFlags
            filters={filters}
            hasExport
            onApplyFilters={handleApplyFilters}
            onFiltersChange={handleFiltersChange}
            onResetFilter={handleResetFilters}
            onExport={exportExternalUserFlagsLogs}
          />
        </div>
      </div>

      <div className='row g-5 g-xl-8 mb-7'>
        <div className='col'>
          <div className='card'>
            <div className='card-body'>
              <div className='card-rounded w-100'>
                <div className='d-flex flex-column'>
                  <div className='text-center p-10'>
                    <p className='fs-4 fw-bold'>
                      Для получения логов изменений флагов, пожалуйста, укажите фильтры и нажмите кнопку "Экспортировать".
                    </p>
                    <p className='text-muted'>
                      Результаты будут загружены в виде Excel-файла.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
